import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";

import PageTitle from "../../layouts/PageTitle";
import { selectHasModuleAccess } from "../../../store/authSlice";
import {
  downloadHotelFeedbackExcel,
  listHotelFeedbackApi,
  listHotelFeedbackMonthsApi,
  saveHotelFeedbackApi,
} from "../../../services/hotelFeedback";
import { RepeatBadge, StarRating, WHITE, errMsg, field, fmtDate, inr, label, monthLabel, panel } from "./config";

// The feedback sheet with the star ratings, call date first. Remarks / stars
// are filled in on the Feedback sheet; the "Negative" tick can be set here
// too. Everything else comes from the upload.

const LIMIT = 25;

const Stat = ({ title, value, tone = "text-gray-900", children }) => (
  <div className="rounded-2xl border border-gray-200 bg-white px-4 py-3">
    <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">{title}</div>
    <div className={`mt-1 text-xl font-bold tabular-nums ${tone}`}>{value}</div>
    {children}
  </div>
);

const FeedbackList = () => {
  const canRead = useSelector(selectHasModuleAccess("hotelFeedback", "read"));
  const canWrite = useSelector(selectHasModuleAccess("hotelFeedback", "read-write"));

  const [q, setQ] = useState("");
  const [search, setSearch] = useState("");
  const [months, setMonths] = useState([]);
  const [month, setMonth] = useState(""); // "" = latest month
  const [rated, setRated] = useState("");
  const [negativeOnly, setNegativeOnly] = useState(false);
  const [flagging, setFlagging] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // debounce the search box
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(q.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [q]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await listHotelFeedbackApi({
        q: search || undefined,
        month: month || undefined,
        rated: rated || undefined,
        negative: negativeOnly ? "yes" : undefined,
        page,
        limit: LIMIT,
      });
      setData(d);
    } catch (err) {
      Swal.fire({ icon: "error", title: "Could not load feedback", text: errMsg(err) });
    } finally {
      setLoading(false);
    }
  }, [search, month, rated, negativeOnly, page]);

  useEffect(() => {
    if (!canRead) return;
    listHotelFeedbackMonthsApi()
      .then(({ data: d }) => setMonths(d.months || []))
      .catch(() => {});
  }, [canRead]);

  useEffect(() => {
    if (canRead) load();
  }, [canRead, load]);

  if (!canRead) {
    return (
      <>
        <PageTitle activeMenu="Feedback List" motherMenu="Hotel Feedback" />
        <div className={`${panel} p-10 text-center`}>
          <h3 className="text-lg font-semibold text-gray-900">
            You don't have access to Hotel Feedback.
          </h3>
        </div>
      </>
    );
  }

  // Same filters as the table (month, search, rated) — every page, not just this one.
  const download = async () => {
    setDownloading(true);
    try {
      await downloadHotelFeedbackExcel({
        month: month || data?.month || undefined,
        q: search || undefined,
        rated: rated || undefined,
        negative: negativeOnly ? "yes" : undefined,
      });
    } catch (err) {
      Swal.fire({ icon: "error", title: "Download failed", text: errMsg(err) });
    } finally {
      setDownloading(false);
    }
  };

  const items = data?.items || [];
  const st = data?.stats;
  const resetFilters = () => {
    setQ("");
    setMonth("");
    setRated("");
    setNegativeOnly(false);
    setPage(1);
  };

  // Tick / untick "Negative" straight from the list.
  const toggleNegative = async (r) => {
    setFlagging(r._id);
    try {
      await saveHotelFeedbackApi([{ id: r._id, negative: !r.negative }]);
      await load();
    } catch (err) {
      Swal.fire({ icon: "error", title: "Could not update", text: errMsg(err) });
    } finally {
      setFlagging("");
    }
  };

  return (
    <>
      <PageTitle activeMenu="Feedback List" motherMenu="Hotel Feedback" />

      {data?.month && (
        <h2 className="mb-3 text-lg font-semibold text-gray-900">{monthLabel(data.month)}</h2>
      )}

      <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <Stat title="Guests" value={st?.total ?? "—"} />
        <Stat title="Feedback done" value={st?.rated ?? "—"} tone="text-emerald-700" />
        <Stat title="Pending" value={st?.pending ?? "—"} tone="text-amber-600" />
        <Stat title="Average rating" value={st?.avgRating != null ? st.avgRating.toFixed(1) : "—"}>
          {st?.avgRating != null && (
            <div className="mt-1">
              <StarRating value={st.avgRating} size={14} showValue={false} />
            </div>
          )}
        </Stat>
        <button
          type="button"
          onClick={() => {
            setNegativeOnly((v) => !v);
            setPage(1);
          }}
          className="text-left"
          title="Show only negative feedback"
        >
          <Stat title="Negative feedback" value={st?.negative ?? "—"} tone="text-red-600">
            <div className="mt-1 text-[11px] text-red-500">
              {negativeOnly ? "Showing negatives only — click to show all" : "Click to show only these"}
            </div>
          </Stat>
        </button>
      </div>

      <section className={`${panel} mb-4 flex flex-wrap items-end gap-3 px-5 py-4`}>
        <div className="min-w-[220px] flex-1">
          <span className={label}>Search</span>
          <input
            className={field}
            style={WHITE}
            placeholder="Name, phone, room, remarks…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <div>
          <span className={label}>Month (check-in)</span>
          <select
            className={field}
            style={WHITE}
            value={month || data?.month || ""}
            onChange={(e) => {
              setMonth(e.target.value);
              setPage(1);
            }}
          >
            {months.length === 0 && <option value="">—</option>}
            {months.map((m) => (
              <option key={m.month} value={m.month}>
                {monthLabel(m.month)} ({m.total})
              </option>
            ))}
            <option value="all">All months</option>
          </select>
        </div>
        <div>
          <span className={label}>Rating</span>
          <select className={field} style={WHITE} value={rated} onChange={(e) => { setRated(e.target.value); setPage(1); }}>
            <option value="">All</option>
            <option value="yes">Feedback done</option>
            <option value="no">Pending</option>
          </select>
        </div>
        <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">
          <input
            type="checkbox"
            className="h-4 w-4 accent-red-600"
            checked={negativeOnly}
            onChange={(e) => {
              setNegativeOnly(e.target.checked);
              setPage(1);
            }}
          />
          Negative only
        </label>
        <button
          type="button"
          onClick={resetFilters}
          className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50"
        >
          Reset
        </button>
        <button
          type="button"
          onClick={download}
          disabled={downloading || !data?.total}
          className="ml-auto rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-emerald-700 transition hover:bg-emerald-100 disabled:opacity-50"
        >
          {downloading ? "Preparing…" : "⬇ Download Excel"}
        </button>
        {canWrite && (
          <Link
            to="/hotel-feedback"
            className="rounded-lg bg-amber-500 px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-slate-950 hover:bg-amber-400"
          >
            Open Feedback sheet →
          </Link>
        )}
      </section>

      <section className={`${panel} overflow-hidden`}>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
              <tr>
                <th className="px-3 py-2 text-left">SR.NO</th>
                <th className="whitespace-nowrap px-3 py-2 text-left">Call Date</th>
                <th className="px-3 py-2 text-left">Rating</th>
                <th className="px-3 py-2 text-center">Negative</th>
                <th className="px-3 py-2 text-left">Name</th>
                <th className="px-3 py-2 text-left">Phone No.</th>
                <th className="whitespace-nowrap px-3 py-2 text-left">Check-In</th>
                <th className="whitespace-nowrap px-3 py-2 text-left">Check-Out</th>
                <th className="px-3 py-2 text-left">Guest Status</th>
                <th className="px-3 py-2 text-left">Reservation Stay Duration</th>
                <th className="px-3 py-2 text-left">Room Type</th>
                <th className="px-3 py-2 text-left">Room</th>
                <th className="px-3 py-2 text-left">Rate / Package</th>
                <th className="px-3 py-2 text-right">Average Rate</th>
                <th className="px-3 py-2 text-left">Remark's</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading && items.length === 0 && (
                <tr>
                  <td colSpan={15} className="px-4 py-10 text-center text-gray-400">Loading…</td>
                </tr>
              )}
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={15} className="px-4 py-10 text-center text-gray-400">
                    No feedback found.
                  </td>
                </tr>
              )}
              {items.map((r, idx) => (
                <tr
                  key={r._id}
                  className={`align-top ${
                    r.negative
                      ? "border-l-4 border-l-red-500 bg-red-50 hover:bg-red-100/70"
                      : r.repeat
                        ? "bg-rose-100 hover:bg-rose-200/70"
                        : "hover:bg-gray-50/60"
                  }`}
                >
                  <td className="px-3 py-2 tabular-nums text-gray-500">{(page - 1) * LIMIT + idx + 1}</td>
                  <td className="whitespace-nowrap px-3 py-2 font-medium text-gray-800">
                    {r.date || r.ratedAt ? fmtDate(r.date || r.ratedAt) : <span className="text-gray-400">Not called</span>}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2">
                    <StarRating value={r.rating} />
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-center">
                    {canWrite ? (
                      <input
                        type="checkbox"
                        className="h-4 w-4 cursor-pointer accent-red-600"
                        checked={!!r.negative}
                        disabled={flagging === r._id}
                        onChange={() => toggleNegative(r)}
                        aria-label={`Mark ${r.name}'s feedback as negative`}
                      />
                    ) : null}
                    {r.negative && (
                      <div className="mt-1 rounded-full bg-red-600 px-2 py-0.5 text-[10px] font-bold text-white">
                        NEGATIVE
                      </div>
                    )}
                  </td>
                  <td className={`whitespace-nowrap px-3 py-2 font-medium ${r.negative ? "text-red-800" : r.repeat ? "text-rose-800" : "text-gray-900"}`}>
                    {r.name}
                    <RepeatBadge repeat={r.repeat} />
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 tabular-nums text-gray-700">{r.phone || "—"}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-gray-600">{fmtDate(r.checkInDate)}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-gray-600">{fmtDate(r.checkOutDate)}</td>
                  <td className="px-3 py-2">
                    {r.guestStatus ? (
                      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-700">
                        {r.guestStatus}
                      </span>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-xs text-gray-600">{r.stayDuration || "—"}</td>
                  <td className="px-3 py-2 text-gray-700">{r.roomType || "—"}</td>
                  <td className="px-3 py-2 tabular-nums text-gray-700">{r.room || "—"}</td>
                  <td className="px-3 py-2 text-gray-700">{r.ratePackage || "—"}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-gray-700">{inr(r.averageRate)}</td>
                  <td
                    className={`min-w-[200px] max-w-[320px] px-3 py-2 text-xs ${
                      r.negative ? "font-medium text-red-800" : "text-gray-700"
                    }`}
                  >
                    {r.remarks || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {data && data.pages > 1 && (
          <div className="flex items-center justify-between border-t border-gray-100 px-5 py-3 text-xs text-gray-500">
            <span>
              Page {data.page} of {data.pages} · {data.total} guests
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="rounded-lg border border-gray-300 bg-white px-3 py-1 font-semibold disabled:opacity-40"
              >
                ‹ Prev
              </button>
              <button
                type="button"
                disabled={page >= data.pages}
                onClick={() => setPage((p) => p + 1)}
                className="rounded-lg border border-gray-300 bg-white px-3 py-1 font-semibold disabled:opacity-40"
              >
                Next ›
              </button>
            </div>
          </div>
        )}
      </section>
    </>
  );
};

export default FeedbackList;
